import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { mxn, STATUS, STATUS_ORDER, VERTICAL, LOST, SegBadge } from "@/lib/format";

const inp = "w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF7A00]";

export const LostReasonDialog = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState("cliente_desistio");
  const [detail, setDetail] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lost-reason-dialog"><DialogHeader><DialogTitle className="font-display">Motivo de pérdida</DialogTitle></DialogHeader>
        <p className="text-sm text-[#575757]">Registra por qué se perdió la operación. Alimenta la analítica de la franquicia.</p>
        <select data-testid="lost-reason-select" className={inp} value={reason} onChange={(e) => setReason(e.target.value)}>{Object.entries(LOST).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <textarea data-testid="lost-reason-detail" className={inp} rows={3} placeholder="Detalle (opcional)" value={detail} onChange={(e) => setDetail(e.target.value)} />
        <div className="flex gap-2 justify-end"><button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold hover:bg-muted">Cancelar</button><button data-testid="lost-reason-confirm" onClick={() => onConfirm(reason, detail)} className="rounded-full px-4 py-2 text-sm font-semibold bg-[#E5484D] text-white">Marcar como perdido</button></div>
      </DialogContent>
    </Dialog>
  );
};

export const Kanban = ({ leads, onChanged, showOwner }) => {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [pendingLost, setPendingLost] = useState(null);

  const move = async (id, status, extra = {}) => {
    try { await api.patch(`/leads/${id}`, { status, ...extra }); toast.success(`Movido a ${STATUS[status].label}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const drop = (status) => {
    setOver(null);
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.status === status) return;
    if (status === "perdido") { setPendingLost(lead.id); return; }
    move(lead.id, status);
  };

  return (
    <>
      <div data-testid="kanban-board" className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
        {STATUS_ORDER.map((s) => {
          const col = leads.filter((l) => l.status === s);
          const total = col.reduce((a, l) => a + (l.amount || 0), 0);
          return (
            <div key={s} data-testid={`kanban-col-${s}`} onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver(null)} onDrop={() => drop(s)}
              className={`shrink-0 w-[260px] rounded-2xl bg-[#FAF8F5] border transition-colors ${over === s ? "border-[#FF7A00] bg-[#FFF6EC]" : "border-border"}`}>
              <div className="px-3 pt-3 pb-2 flex items-center justify-between"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS[s].cls}`}>{STATUS[s].label}</span><span className="text-[11px] text-[#8A8A8A] flex items-center gap-1.5"><span className="rounded-full bg-white border border-border px-1.5 font-semibold text-[#0B0B0B]">{col.length}</span>{mxn(total)}</span></div>
              <div className="px-2 pb-2 space-y-2 min-h-[120px]">
                {col.length === 0 && <div className="rounded-xl border border-dashed border-border text-[11px] text-[#8A8A8A] text-center py-6">Arrastra un lead aquí</div>}
                {col.map((l) => (
                  <div key={l.id} data-testid="kanban-card" draggable onDragStart={() => setDragId(l.id)} onDragEnd={() => setDragId(null)}
                    className={`rounded-xl bg-white border border-black/[.06] p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-[#FF7A00]/50 transition-[transform,box-shadow,border-color] ${dragId === l.id ? "opacity-50" : ""}`}>
                    <div className="flex items-start gap-2.5"><div className="h-8 w-8 shrink-0 rounded-full bg-[#FFF6EC] text-[#FF7A00] grid place-items-center text-[11px] font-display font-bold">{(l.name || "?").split(" ").slice(0, 2).map((x) => x[0]).join("").toUpperCase()}</div><div className="min-w-0 flex-1"><Link to={`/app/leads/${l.id}`} className="font-semibold text-sm hover:text-[#FF7A00] block truncate">{l.name}</Link>
                    <div className="text-[11px] text-[#8A8A8A] mt-0.5 flex items-center gap-1.5 flex-wrap">{VERTICAL[l.vertical]} · {l.product || "—"} <SegBadge s={l.segment} /></div></div></div>
                    <div className="flex items-center justify-between mt-2"><span className="font-display font-bold text-sm">{l.amount ? mxn(l.amount) : "—"}</span>{showOwner && <span className="text-[10px] text-[#8A8A8A] truncate max-w-[110px]">{l.advisor_name}</span>}</div>
                    {l.status === "perdido" && l.lost_reason && <div className="mt-1 text-[10px] text-red-700">{LOST[l.lost_reason]}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <LostReasonDialog open={!!pendingLost} onClose={() => setPendingLost(null)} onConfirm={(reason, detail) => { move(pendingLost, "perdido", { lost_reason: reason, lost_detail: detail || undefined }); setPendingLost(null); }} />
    </>
  );
};
